import { ModalButtonStyles, OpenModal } from '@composables/common'
import { Asset, ContractTemplate } from '@src/types'
import { Ref, computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ContractTemplateEditForm from '@partials/oekostrom/ContractTemplateEditForm.vue'

export function useContractTemplateManager(
    asset: Ref<Asset | null>,
    template: Ref<ContractTemplate | null>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const i18n = useI18n()

    /**
     * Trigger the confirmation flow to remove the current template.
     */
    function handleDeleteTemplateConfirmation() {
        if (!template.value && !asset.value?.sm_template) return

        isModalOpen.value = true
        openModal(
            {
                id: 'delete_contract_template',
                title: 'modal_title_delete_contract_template',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteTemplate(),
            },
            // Direct text or simple layout node instead of table row collection
            () =>
                h('div', { class: 'p-4 text-body' }, [
                    i18n.t('modal_delete_warning_contract_template', {
                        name: template.value?.name,
                    }),
                ]),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    /**
     * Reset the template state to null and sync with backend.
     */
    function deleteTemplate() {
        template.value = null
        if (asset.value) {
            asset.value.sm_template = null
        }
        isModalOpen.value = false
    }

    /**
     * Open the modal form to upload a new contract template or replace the current one.
     */
    function handleUpdateOrCreateTemplate() {
        // 1. Keep the object inside a single reactive ref
        const editedTemplate = ref<Partial<ContractTemplate>>({
            name: template.value?.name || '',
            upload_key: '',
            custom_properties: template.value?.custom_properties || {},
        })

        isModalOpen.value = true

        const disableSubmit = computed(() => {
            return !(
                editedTemplate.value.name && editedTemplate.value.upload_key
            )
        })

        // 2. Call openModal strictly matching your team's structural signature
        openModal(
            {
                id: 'edit_contract_template',
                title: template.value
                    ? 'modal_title_edit_contract_template'
                    : 'modal_title_create_contract_template',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => {
                    if (asset.value) {
                        asset.value.sm_template =
                            editedTemplate.value as ContractTemplate
                    }
                    isModalOpen.value = false
                },
            },
            // Content callback: Pass the ref directly so Vue's internal binding handles updates flawlessly
            () =>
                h(ContractTemplateEditForm, {
                    modelValue: editedTemplate.value,
                }),
            {
                submitButtonText: 'save',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableSubmit,
            }
        )
    }

    const hasTemplate = computed(
        () => template.value !== null || asset.value?.sm_template !== null
    )

    return {
        hasTemplate,
        handleDeleteTemplateConfirmation,
        handleUpdateOrCreateTemplate,
    }
}
